import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { to: "/", label: "Home", id: "nav-home-link" },
  { to: "/pacchetti", label: "Pacchetti", id: "nav-catalog-link" },
  { to: "/calcolatore-roi", label: "Calcolatore ROI", id: "nav-calculator-link" },
  { to: "/chi-siamo", label: "Chi Siamo", id: "nav-about-link" },
];

export const Navbar = () => {
  const { count } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-lineblue" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="nav-logo" className="shrink-0">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `relative font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 hover:text-electric ${
                    isActive ? "text-electric" : "text-slate-300"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/carrello"
              data-testid="nav-cart-button"
              className="relative flex items-center gap-2 rounded-full border border-lineblue bg-surface/60 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-200 transition-all duration-300 hover:border-glow hover:text-electric"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Carrello</span>
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    key={count}
                    data-testid="cart-badge-count"
                    initial={{ scale: 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.4, opacity: 0 }}
                    className="absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-electric px-1 font-sans text-[10px] font-bold text-ink"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>

            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden rounded-full border border-lineblue p-2.5 text-slate-200"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden glass border-t border-lineblue"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.id}
                  className={({ isActive }) =>
                    `font-display text-2xl font-semibold ${
                      isActive ? "text-electric" : "text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
